// src/utils/api.js
// Shared across App.jsx, AuthPage.jsx, and any other component that hits the backend.
// Access token lives in memory only — never localStorage.

const API = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

let _accessToken = null;
let _refreshPromise = null;

export const setAccessToken   = (t) => { _accessToken = t; };
export const getAccessToken   = ()  => _accessToken;
export const clearAccessToken = ()  => { _accessToken = null; };

// ─── Bootstrap refresh (called once on app mount) ────────────────────────────
// Retries up to MAX_RETRIES times on network errors before giving up.
// Returns { accessToken, user } on success, throws on genuine failure.
const MAX_RETRIES   = 4;
const RETRY_DELAY   = 800; // ms between retries (× attempt, so 800/1600/2400/3200ms)
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export async function bootstrapRefresh(attempt = 0) {
  try {
    const res = await fetch(`${API}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    // 401 / 403 = no valid cookie — genuinely not logged in, don't retry
    if (res.status === 401 || res.status === 403) {
      throw Object.assign(new Error('No session'), { status: res.status, noSession: true });
    }
    // 5xx = server crash — retry, don't treat as "logged out"
    if (res.status >= 500) {
      throw Object.assign(new Error(`Server error (${res.status})`), { status: res.status, serverError: true });
    }
    if (!res.ok) throw Object.assign(new Error(`Refresh failed (${res.status})`), { status: res.status });
    return await res.json(); // { accessToken, user?, ... }
  } catch (err) {
    // Retry on: network failures (no err.status) OR server 5xx errors
    const shouldRetry = (!err.status || err.serverError) && attempt < MAX_RETRIES;
    if (shouldRetry) {
      await sleep(RETRY_DELAY * (attempt + 1));
      return bootstrapRefresh(attempt + 1);
    }
    throw err;
  }
}

// ─── Core fetch wrapper ──────────────────────────────────────────────────────
export async function api(path, options = {}, _retry = true) {
  // Network-level fetch — don't let a transient error kill the session
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(_accessToken ? { Authorization: `Bearer ${_accessToken}` } : {}),
        ...options.headers,
      },
    });
  } catch (networkErr) {
    // fetch() itself threw (offline, DNS, CORS preflight) — surface as a
    // plain error without touching the session
    const err = new Error('Network error — please check your connection');
    err.status = 0;
    throw err;
  }

  const isAuthRoute = path.startsWith('/auth/');

  if (res.status === 401 && _retry && !isAuthRoute) {
    const body = await res.json().catch(() => ({}));

    // Only attempt silent refresh for token problems, not for e.g. wrong password
    const isTokenError = (
      body.code === 'TOKEN_EXPIRED'          ||
      body.error === 'No token provided'     ||
      body.error === 'Invalid token'         ||
      body.error === 'jwt expired'           ||
      body.error === 'TokenExpiredError'
    );

    if (isTokenError) {
      // Deduplicate: if a refresh is already in-flight, wait for it
      if (!_refreshPromise) {
        _refreshPromise = fetch(`${API}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        })
          .then(async r => {
            const d = await r.json().catch(() => ({}));
            // Attach http status so we can distinguish 401 from network errors below
            d._status = r.status;
            return d;
          })
          .finally(() => { _refreshPromise = null; });
      }

      let refreshData;
      try {
        refreshData = await _refreshPromise;
      } catch {
        // refresh fetch itself failed (offline) — do NOT log out, just bubble up
        const err = new Error('Network error — please check your connection');
        err.status = 0;
        throw err;
      }

      if (refreshData?.accessToken) {
        setAccessToken(refreshData.accessToken);
        return api(path, options, false); // replay original request
      }

      // Refresh returned a non-200 (e.g. 401 cookie expired) → genuine logout
      if (refreshData?._status === 401 || refreshData?._status === 403) {
        clearAccessToken();
        window.dispatchEvent(new Event('kfx:logout'));
        const err = new Error('Session expired — please sign in again');
        err.status = 401;
        throw err;
      }

      // Refresh returned some other server error — don't log out, just fail the call
      const err = new Error('Could not refresh session — please try again');
      err.status = refreshData?._status ?? 500;
      throw err;
    }

    // 401 but NOT a token error (e.g. wrong password on /auth/login) — just throw
    const err = new Error(body.error || 'Unauthorized');
    err.status = 401;
    throw err;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || data.message || `HTTP ${res.status}`);
    err.status           = res.status;
    err.errors           = data.errors;
    err.remainingMin     = data.remainingMin;
    // Pass through email-verification fields so AuthPage can redirect to verify screen
    err.code             = data.code;
    err.userId           = data.userId;
    err.needsVerification= data.needsVerification;
    throw err;
  }
  return data;
}

// ─── Auth helpers ────────────────────────────────────────────────────────────
export const authApi = {
  // Uses bootstrapRefresh (with retries) — this is what App.jsx calls on mount
  refresh:  ()                              => bootstrapRefresh(),
  // Logout is fire-and-forget — don't let a 401 on /auth/logout trigger another refresh loop
  logout: async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore — cookie will expire naturally */ }
    clearAccessToken();
  },
  login:    (email, password)               => api('/auth/login',    { method: 'POST', body: JSON.stringify({ email, password }) }),
  // ref is the referral code (KFX-XXXXXXXX) — optional, sent only when present
  register: (name, email, password, ref)    => api('/auth/register', { method: 'POST', body: JSON.stringify({ name, email, password, ...(ref ? { ref } : {}) }) }),
  me:       ()                              => api('/auth/me'),

  // ── Email verification ────────────────────────────────────────
  // Called after register or after a failed login on unverified account
  verifyEmail: (userId, code) => api('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ userId, code }),
  }),
  // Resend the 6-digit OTP to the user's email
  resendVerification: (userId) => api('/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  }),
};


// ─── Public API ───────────────────────────────────────────────────────────────
export const publicApi = {
  getActiveAnnouncements: () => api('/announcements/active'),
};

// ─── Referral API ─────────────────────────────────────────────────────────────
export const referralApi = {
  dashboard: () => api('/referral/dashboard'),
  withdraw:  (amount, method, details) => api('/referral/withdraw', {
    method: 'POST',
    body: JSON.stringify({ amount, method, details }),
  }),
};

// ─── Admin API ────────────────────────────────────────────────────────────────
// Used by admin portal pages — all calls include Bearer token automatically
export const adminApi = {
  getOverview:    ()            => api('/admin/stats/overview'),
  getUsers:       (params = {}) => api(`/admin/users?${new URLSearchParams(params)}`),
  getLocations:   ()            => api('/admin/stats/locations'),
  getDevices:     ()            => api('/admin/stats/devices'),
  getReferrals:   (params = {}) => api(`/admin/referrals?${new URLSearchParams(params)}`),
  getProfile:     ()            => api('/admin/profile'),
  updateProfile:  (body)        => api('/admin/settings/profile',  { method: 'PUT', body: JSON.stringify(body) }),
  updatePassword: (body)        => api('/admin/settings/password', { method: 'PUT', body: JSON.stringify(body) }),
  getUserDetail:  (id)           => api(`/admin/users/${id}`),
  suspendUser:    (id)           => api(`/admin/users/${id}/suspend`, { method: 'PATCH' }),
  deleteUser:     (id)           => api(`/admin/users/${id}`, { method: 'DELETE' }),
  setUserRole:    (id, role)     => api(`/admin/users/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  getRevenue:     ()             => api('/admin/revenue'),
  getUsage:       ()             => api('/admin/usage'),
  getActiveSessions:    ()    => api('/admin/sessions/active'),
  getSuspiciousActivity:()    => api('/admin/sessions/suspicious'),
  getUserSessions:      (id)  => api(`/admin/users/${id}/sessions`),
  revokeUserSessions:   (id)  => api(`/admin/users/${id}/sessions`,  { method: 'DELETE' }),
  revokeSession:        (tid) => api(`/admin/sessions/${tid}`,        { method: 'DELETE' }),
  getHealth:            ()    => api('/admin/health'),
  getAnnouncements:     ()       => api('/admin/announcements'),
  createAnnouncement:   (body)   => api('/admin/announcements',      { method: 'POST',   body: JSON.stringify(body) }),
  updateAnnouncement:   (id, b)  => api(`/admin/announcements/${id}`,{ method: 'PUT',    body: JSON.stringify(b) }),
  deleteAnnouncement:   (id)     => api(`/admin/announcements/${id}`,{ method: 'DELETE' }),
  getInstruments:       ()       => api('/admin/instruments'),
  createInstrument:     (body)   => api('/admin/instruments',        { method: 'POST',   body: JSON.stringify(body) }),
  updateInstrument:     (id, b)  => api(`/admin/instruments/${id}`,  { method: 'PUT',    body: JSON.stringify(b) }),
  deleteInstrument:     (id)     => api(`/admin/instruments/${id}`,  { method: 'DELETE' }),
  getEmailBlastHistory: ()       => api('/admin/marketing/email-blast'),
  sendEmailBlast:       (body)   => api('/admin/marketing/email-blast', { method: 'POST', body: JSON.stringify(body) }),
  deleteEmailBlast:     (id)     => api(`/admin/marketing/email-blast/${id}`, { method: 'DELETE' }),

  // ── Feature-gate config ──────────────────────────────────────
  getFeatureConfig:     ()         => api('/admin/feature-config'),
  saveFeatureConfig:    (body)     => api('/admin/feature-config',     { method: 'POST',  body: JSON.stringify(body) }),

  // ── User plan management ─────────────────────────────────────
  setUserPlan:          (id, plan) => api(`/admin/users/${id}/plan`,   { method: 'PATCH', body: JSON.stringify({ plan }) }),
};

// ─── Trade mappers ────────────────────────────────────────────────────────────
export function mapTrade(t) {
  return {
    id:       t.id,
    pair:     t.symbol       ?? '',
    dir:      t.direction    ?? 'BUY',
    date:     t.tradeDate    ? new Date(t.tradeDate).toISOString().slice(0, 10) : '',
    time:     t.tradeDate    ? new Date(t.tradeDate).toISOString().slice(11, 16) : '',
    exitDate: t.closedAt     ? new Date(t.closedAt).toISOString().slice(0, 10) : '',
    exitTime: t.closedAt     ? new Date(t.closedAt).toISOString().slice(11, 16) : '',
    entry:    t.entryPrice   ?? '',
    sl:       t.stopLoss     ?? '',
    tp:       t.takeProfit   ?? '',
    lotSize:  t.lotSize      ?? 0,
    pips:     0,
    result:   Number(t.pnl  ?? 0),
    rr:       t.rrRatio      ?? '0.00',
    strat:    t.strategy     ?? '',
    session:  t.session      ?? '',
    emotion:  t.emotion      ?? '',
    notes:    t.notes        ?? '',
    review:   t.mistakes     ?? '',
    img:      t.screenshot   ?? '',
    status:   t.status       ?? 'CLOSED',
    imgBefore: t.imgBefore   ?? t.screenshot ?? '',
    imgAfter:  t.imgAfter    ?? '',
    confluences: (t.confluenceTags ?? []).map(ct => ct.tag?.name ?? ct),

    actualEntry:  t.actualEntry  != null ? t.actualEntry  : '',
    actualExit:   t.actualExit   != null ? t.actualExit   : '',
    actualRR:     t.actualRR     != null ? t.actualRR     : '',
    mfe:          t.mfe          != null ? t.mfe          : '',
    mae:          t.mae          != null ? t.mae          : '',
    entryType:    t.entryType    ?? '',
    exitType:     t.exitType     ?? '',
    setupGrade:   t.setupGrade   ?? '',
    movedSl:      t.movedSl      ?? false,
    partialClose: t.partialClose ?? false,
    heldToTp:     t.heldToTp     ?? false,
    ruleChecks:   Array.isArray(t.ruleChecks) ? t.ruleChecks : [],
    newsPresent:  t.newsPresent ?? false,
    accountId:    t.account_id  ?? null,   // DB column is snake_case; expose as camelCase to frontend

    // ── Fields previously missing from mapTrade ────────────
    exitPrice:       t.exitPrice       ?? '',
    timeframe:       t.timeframe       ?? '',
    htfBias:         t.htfBias         ?? '',
    htfTf:           t.htfTf           ?? '',
    entryTf:         t.entryTf         ?? '',
    marketCondition: t.marketCondition ?? '',
    followedPlan:    t.followedPlan    ?? '',
    plannedRR:       t.plannedRR       ?? '',
    riskAmount:      t.riskAmount      ?? '',
    riskPct:         t.riskPct         ?? '',
    accountSize:     t.accountSize     ?? '',
    duration:        t.duration        ?? '',
    confidence:      t.confidence      ?? 0,
    discipline:      t.discipline      ?? 0,
    stressLevel:     t.stressLevel     ?? 0,
    pips:            t.pips            ?? 0,
  };
}

export function unmapTrade(local) {
  return {
    symbol:      (local.pair      || '').toUpperCase(),
    direction:   local.dir        || 'BUY',
    status:      local.status     || 'CLOSED',
    tradeDate:   local.date
                   ? new Date(`${local.date}T${local.time || '00:00'}:00Z`).toISOString()
                   : new Date().toISOString(),
    // Use the explicit exitDate when provided (swing trades / multi-day holds).
    // Fall back to the overnight heuristic only when no exit date is set:
    // if exitTime < time on the same day, the exit is the following calendar day.
    closedAt: (() => {
      if (!local.exitTime || !local.date) return null;
      let exitDate = local.exitDate || local.date;
      if (!local.exitDate && local.time && local.exitTime < local.time) {
        const d = new Date(local.date);
        d.setDate(d.getDate() + 1);
        exitDate = d.toISOString().slice(0, 10);
      }
      return new Date(`${exitDate}T${local.exitTime}:00Z`).toISOString();
    })(),

    entryPrice:  local.entry    != null && local.entry    !== '' ? Number(local.entry)    : null,
    stopLoss:    local.sl       != null && local.sl       !== '' ? Number(local.sl)       : null,
    takeProfit:  local.tp       != null && local.tp       !== '' ? Number(local.tp)       : null,
    lotSize:     local.lotSize  != null && local.lotSize  !== 0  ? Number(local.lotSize)  : null,

    pnl:         local.result   != null                          ? Number(local.result)   : null,
    rrRatio:     local.rr       != null && local.rr       !== '' ? Number(local.rr)       : null,

    actualEntry:  local.actualEntry  != null && local.actualEntry  !== '' ? Number(local.actualEntry)  : null,
    actualExit:   local.actualExit   != null && local.actualExit   !== '' ? Number(local.actualExit)   : null,
    actualRR:     local.actualRR     != null && local.actualRR     !== '' ? Number(local.actualRR)     : null,
    mfe:          local.mfe          != null && local.mfe          !== '' ? Number(local.mfe)          : null,
    mae:          local.mae          != null && local.mae          !== '' ? Number(local.mae)          : null,
    entryType:    local.entryType    || null,
    exitType:     local.exitType     || null,
    setupGrade:   local.setupGrade   || null,
    movedSl:      local.movedSl      ?? false,
    partialClose: local.partialClose ?? false,
    heldToTp:     local.heldToTp     ?? false,
    ruleChecks:   Array.isArray(local.ruleChecks) ? local.ruleChecks : [],

    screenshot:  local.imgBefore != null ? (local.imgBefore || null) : (local.img || null),
    imgBefore:   local.imgBefore !== undefined ? (local.imgBefore || null) : undefined,
    imgAfter:    local.imgAfter  !== undefined ? (local.imgAfter  || null) : undefined,

    strategy:    local.strat    || local.strategy || null,
    session:     local.session  || null,
    emotion:     local.emotion  || null,
    notes:       local.notes    || null,
    mistakes:    local.review   || null,
    confluenceTags: local.confluences ?? local.confluenceTags ?? [],
    newsPresent:   local.newsPresent ?? false,
    accountId:     local.accountId  || null,

    // ── Fields previously missing from unmapTrade ──────────
    // actual exit price fills the exit_price DB column; fall back to exitPrice if explicitly set
    exitPrice:       local.actualExit  != null && local.actualExit  !== '' ? Number(local.actualExit)  :
                     local.exitPrice   != null && local.exitPrice   !== '' ? Number(local.exitPrice)   : null,
    timeframe:       local.entryTf     || local.timeframe || null,  // entryTf drives the DB timeframe column
    htfBias:         local.htfBias     || null,
    htfTf:           local.htfTf       || null,
    entryTf:         local.entryTf     || null,
    marketCondition: local.marketCondition || null,
    followedPlan:    local.followedPlan != null && local.followedPlan !== '' ? local.followedPlan : null,
    plannedRR:       local.plannedRR   != null && local.plannedRR   !== '' ? Number(local.plannedRR)   : null,
    riskAmount:      local.riskAmount   != null && local.riskAmount  !== '' ? Number(local.riskAmount)  : null,
    riskPct:         local.riskPct     != null && local.riskPct     !== '' ? Number(local.riskPct)     : null,
    accountSize:     local.accountSize != null && local.accountSize !== '' ? Number(local.accountSize) : null,
    duration:        local.duration    || null,
    confidence:      local.confidence  > 0 ? local.confidence  : null,
    discipline:      local.discipline  > 0 ? local.discipline  : null,
    stressLevel:     local.stressLevel > 0 ? local.stressLevel : null,
    pips:            local.pips        != null                          ? Number(local.pips)           : null,
  };
}

export const tradesApi = {
  /**
   * Fetch pre-computed stats from the server.
   * Returns the same shape as calcStats() in helpers.js so existing consumers
   * can drop-in replace without changing stat-card rendering logic.
   *
   * @param {string|null} accountId  Optional account filter
   */
  async stats(accountId = null) {
    const accountParam = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    return api(`/trades/stats${accountParam}`);
  },

  async list(accountId = null) {
    let allTrades = [];
    let page = 1;
    let total = Infinity;
    const accountParam = accountId ? `&accountId=${encodeURIComponent(accountId)}` : '';

    while (allTrades.length < total) {
      const data = await api(`/trades?page=${page}&limit=200${accountParam}`);
      allTrades = [...allTrades, ...(data.trades ?? []).map(mapTrade)];
      total = data.total ?? 0;
      if ((data.trades ?? []).length < 200) break;
      page++;
    }

    return allTrades;
  },

  async create(localTrade) {
    const data = await api('/trades', { method: 'POST', body: JSON.stringify(unmapTrade(localTrade)) });
    return mapTrade(data.trade);
  },

  async update(id, localTrade) {
    const data = await api(`/trades/${id}`, { method: 'PUT', body: JSON.stringify(unmapTrade(localTrade)) });
    return mapTrade(data.trade);
  },

  async delete(id) {
    return api(`/trades/${id}`, { method: 'DELETE' });
  },

  // Returns the number of trades with no account assigned (legacy data)
  async legacyCount() {
    const data = await api('/trades/legacy-count');
    return data.count ?? 0;
  },

  // Bulk-assigns all NULL-account trades to the given accountId
  async claimLegacy(accountId) {
    return api('/trades/claim-legacy', {
      method: 'POST',
      body: JSON.stringify({ accountId }),
    });
  },

  async import(localTrades) {
    return api('/trades/import', {
      method: 'POST',
      body: JSON.stringify({ trades: localTrades.map(unmapTrade) }),
    });
  },
};